import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProject, ProjectAccess, ProjectAccessUser } from '../customer-project.interface';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { CustomerService } from '../services/customer/customer.service';
import { PageTitleService } from '../services/page-title/page-title.service';

@Component({
    selector: 'app-customer-project-manage',
    templateUrl: './customer-project-manage.component.html',
    standalone: false,
})
export class CustomerProjectManageComponent extends CodaltComponent implements OnInit {
    customerId: number;
    customer: Customer;
    project: CustomerProject;

    access?: ProjectAccess;
    pending: ProjectAccessUser[] = [];
    active: ProjectAccessUser[] = [];
    revoked: ProjectAccessUser[] = [];
    grantable: { id: number; name: string }[] = [];
    showRevoked = false;
    addUserIds: number[] = [];

    constructor(
        private customerProjectService: CustomerProjectService,
        private customerService: CustomerService,
        private confirmDialog: ConfirmDialogService,
        private router: Router,
        protected route: ActivatedRoute,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    ngOnInit(): void {
        this.watchRouteParams(
            [this.RouteParamnames.customerId, this.RouteParamnames.projectId],
            ({ customerId, projectId }) => {
                this.customerId = +customerId;

                // Het project pas opvragen zodra het klantgebied in LocalStorage staat: de
                // interceptor stuurt current_customer_id daaruit mee, en getProject() filtert
                // daarop. Bij binnenkomst rechtstreeks op deze route - zonder eerst het project of
                // het overzicht te openen - is die waarde nog leeg of van een ander klantgebied,
                // en geeft de backend een 404 op een project waar je wel toegang toe hebt.
                this.subscriptions.add(
                    this.customerService.getSingle(this.customerId).subscribe(customer => {
                        this.customer = customer;
                        this.LocalStorage.setCustomer(customer);

                        this.subscriptions.add(
                            this.customerProjectService.getProjectDetail(+projectId).subscribe({
                                next: project => {
                                    this.project = project.data;
                                    this.pageTitleService.setTitle('Project beheren', project.data.name);
                                    this.loadAccess();
                                },
                                // Zonder toegang blijft dit scherm anders leeg staan zonder uitleg.
                                error: error => {
                                    this.confirmDialog
                                        .confirm('Project niet beschikbaar', this.Utils.errorText(error), 'Oke')
                                        .then(() => this.close())
                                        .catch(() => this.close());
                                },
                            })
                        );
                    })
                );
            }
        );
    }

    private loadAccess() {
        this.subscriptions.add(
            this.customerProjectService.getProjectAccess(this.project.id).subscribe(access => {
                this.access = access;
                this.pending = access.pending ?? [];
                this.active = access.active ?? [];
                this.revoked = access.revoked ?? [];
                this.grantable = access.grantable ?? [];
            })
        );
    }

    addAccess() {
        if (!this.addUserIds.length) {
            return;
        }
        this.subscriptions.add(
            forkJoin(
                this.addUserIds.map(userId => this.customerProjectService.grantProjectAccess(this.project.id, userId))
            ).subscribe(() => {
                this.addUserIds = [];
                this.loadAccess();
            })
        );
    }

    acceptRequest(user: ProjectAccessUser) {
        this.confirmDialog
            .confirm(
                'Toegang verlenen',
                `Weet je zeker dat je ${user.name} toegang wilt geven tot dit project?`,
                'Toegang geven',
                'Annuleren'
            )
            .then(() =>
                this.subscriptions.add(
                    this.customerProjectService
                        .grantProjectAccess(this.project.id, user.id)
                        .subscribe(() => this.loadAccess())
                )
            )
            .catch(() => {});
    }

    revokeAccess(user: ProjectAccessUser) {
        this.confirmDialog
            .confirm(
                'Toegang intrekken',
                `Weet je zeker dat je de toegang van ${user.name} tot dit project wilt intrekken?`,
                'Intrekken',
                'Behouden'
            )
            .then(() =>
                this.subscriptions.add(
                    this.customerProjectService
                        .revokeProjectAccess(this.project.id, user.id)
                        .subscribe(() => this.loadAccess())
                )
            )
            .catch(() => {});
    }

    rejectRequest(user: ProjectAccessUser) {
        this.confirmDialog
            .confirm(
                'Verzoek afwijzen',
                `Weet je zeker dat je het toegangsverzoek van ${user.name} wilt afwijzen?`,
                'Afwijzen',
                'Behouden'
            )
            .then(() =>
                this.subscriptions.add(
                    this.customerProjectService
                        .revokeProjectAccess(this.project.id, user.id)
                        .subscribe(() => this.loadAccess())
                )
            )
            .catch(() => {});
    }

    toggleEmailNotifications(user: ProjectAccessUser) {
        this.subscriptions.add(
            this.customerProjectService
                .setAccessEmailNotifications(this.project.id, user.id, !user.email_notifications)
                .subscribe(() => this.loadAccess())
        );
    }

    close(project?: CustomerProject) {
        this.router.navigate([
            this.Routenames.customers,
            this.customerId,
            this.Utils.isDrillingRoute(this.route) ? this.Routenames.drillingProjects : this.Routenames.projects,
            ...(project ? [project.id] : []),
        ]);
    }

    delete() {
        this.confirmDialog
            .confirm(
                'Project verwijderen',
                `Weet je zeker dat je het project ${this.project.name} wilt verwijderen?`,
                'Verwijderen',
                'Behouden'
            )
            .then(() => this.customerProjectService.deleteProject(this.project.id).subscribe(() => this.close()))
            .catch(() => {});
    }
}
